// JSON fetch wrapper. Same-origin in production (SPA + API served by the
// Go binary). In dev, Vite proxies /api → backend so cookies still work.
//
// Conventions:
//   - All requests send credentials.
//   - 4xx/5xx throws ApiError with the parsed body.
//   - JSON in/out only — no fragments, no FormData here. (Multipart goes
//     through a separate helper when we need it for /label uploads.)

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type Body = Record<string, unknown> | undefined;

async function request<T>(method: string, path: string, body?: Body): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const parsed = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = (parsed ?? {}) as { code?: string; message?: string; details?: unknown };
    throw new ApiError(
      res.status,
      err.code ?? "unknown",
      err.message ?? `HTTP ${res.status}`,
      err.details,
    );
  }
  return parsed as T;
}

async function requestForm<T>(path: string, fd: FormData): Promise<T> {
  // Critically: do NOT set Content-Type. The browser writes
  // `multipart/form-data; boundary=…` based on the FormData itself.
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    body: fd,
  });

  const text = await res.text();
  const parsed = text ? JSON.parse(text) : null;

  if (!res.ok) {
    const err = (parsed ?? {}) as { code?: string; message?: string; details?: unknown };
    throw new ApiError(
      res.status,
      err.code ?? "unknown",
      err.message ?? `HTTP ${res.status}`,
      err.details,
    );
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: Body) => request<T>("POST", path, body),
  patch: <T>(path: string, body?: Body) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
  postFormData: <T>(path: string, fd: FormData) => requestForm<T>(path, fd),
};

export interface Me {
  user: { id: string; email: string | null } | null;
  players: Array<{ id: string; gameId: string; displayName: string }>;
  build: { sha: string };
}
